/* HerramientasComponent
   Propósito: Captura campos editables y calcula automáticamente:
   - unidadesInstaladas = unidadesRuta * (porcInstaladas / 100)
   - porcInstaladas = (unidadesInstaladas / unidadesRuta) * 100
   - ascensosSemanales = diasAscensosSemana * ascensosPersonaDia
   - ascensosMes = ascensosSemanales * 4.32

   Nota: Se evita el ciclo de actualizaciones con la bandera "syncing".
*/
import { Component, OnInit } from "@angular/core";
import {
	FormBuilder,
	FormGroup,
	Validators,
	AbstractControl,
	ValidationErrors,
} from "@angular/forms";

type Weekday = "LUN" | "MAR" | "MIE" | "JUE" | "VIE" | "SAB" | "DOM";

interface EventoMes {
  day: number;
  turno: "a" | "d";
  unit: number;
}

@Component({
	selector: "app-herramientas",
	templateUrl: "./herramientas.component.html",
	styleUrls: ["./herramientas.component.scss"],
})
export class HerramientasComponent implements OnInit {
	public form!: FormGroup;
	public ascensosMes: number = 0;

	private syncing: boolean = false;

	constructor(private fb: FormBuilder) {}

	/* Inicializa el formulario */
	ngOnInit(): void {
		this.form = this.fb.group({
			/* Campos editables */
			unidadesRuta: [
				40,
				[Validators.required, this.positiveIntValidator],
			],
			porcInstaladas: [
				20,
				[Validators.required, this.percentIntValidator],
			],
			diasAbiertos: [0, [Validators.required, this.percentIntValidator]],
			vistasSemana: [0, [Validators.required, this.percentIntValidator]],
			vistasMes: [0, [Validators.required, this.percentIntValidator]],
			diaInicio: [1, [Validators.required, this.percentIntValidator]],
			diaFinal: [30, [Validators.required, this.percentIntValidator]],
			diasAscensosSemana: [
				6,
				[Validators.required, this.positiveIntValidator],
			],
			ascensosPersonaDia: [
				2,
				[Validators.required, this.positiveIntValidator],
			],

			/* Campos calculados (pero unidadesInstaladas se vuelve editable para poder calcular % desde ahí) */
			unidadesInstaladas: [
				8,
				[Validators.required, this.positiveIntValidator],
			],

			/* Campos calculados */
			ascensosSemanales: [{ value: 0, disabled: true }],
		});

		/* Recalcula ascensos cuando cambian estos campos */
		this.form
			.get("diasAscensosSemana")
			?.valueChanges.subscribe(() => this.recalcularAscensos());
		this.form
			.get("ascensosPersonaDia")
			?.valueChanges.subscribe(() => this.recalcularAscensos());
		this.form
			.get("vistasMes")
			?.valueChanges.subscribe(() => this.recalcularDiasAbiertos());
		this.form
			.get("vistasMes")
			?.valueChanges.subscribe(() => this.recalcularVistasSemana());

		/* Sincronización bidireccional porcentaje <-> unidadesInstaladas (y depende de unidadesRuta) */
		this.form
			.get("porcInstaladas")
			?.valueChanges.subscribe(() => this.syncFromPercent());
		this.form
			.get("unidadesInstaladas")
			?.valueChanges.subscribe(() => this.syncFromUnitsInstalled());
		this.form
			.get("unidadesRuta")
			?.valueChanges.subscribe(() => this.syncFromPercent());

		/* Cálculo inicial */
		this.syncFromPercent();
		this.recalcularAscensos();
	}

	/* Validador: entero >= 0 */
	public positiveIntValidator(
		control: AbstractControl,
	): ValidationErrors | null {
		const value = Number(control.value);
		const ok = Number.isInteger(value) && value >= 0;
		return ok ? null : { positiveInt: true };
	}

	/* Validador: porcentaje entero 0..100 */
	public percentIntValidator(
		control: AbstractControl,
	): ValidationErrors | null {
		const value = Number(control.value);
		const ok = Number.isInteger(value) && value >= 0 && value <= 100;
		return ok ? null : { percentInt: true };
	}

	/* Indica si un control editable es inválido y ya fue tocado */
	public isInvalid(controlName: string): boolean {
		const ctrl = this.form.get(controlName);
		return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
	}

	/* Acción manual (opcional): fuerza validación visual */
	public calcular(): void {
		this.form.markAllAsTouched();
		this.syncFromPercent();
		this.recalcularAscensos();

    const diaInicioCtrl = this.form.get("diaInicio");
    const diaFinCtrl = this.form.get("diaFinal");
    const unidadesRutaCtrl = this.form.get("unidadesRuta"); // maxUnits
    const unidadesInstCtrl = this.form.get("unidadesInstaladas"); // rango 1..unidadesInstaladas
    const vistastCtrl = this.form.get("vistasMes");

    const maxUnits = this.toPositiveInt(unidadesRutaCtrl.value);
    const unidadesInstaladas = this.toPositiveInt(unidadesInstCtrl.value);

    const r = this.contarEventosInstaladosEnRangoDias({
      diaInicio: this.toPositiveInt(diaInicioCtrl.value),
      diaFin: this.toPositiveInt(diaFinCtrl.value),
      maxUnits,
      unidadesInstaladas,
      startUnit: 1
    });

    vistastCtrl.setValue(r.totalEventosEnDiasYUnidades)
	}

	/* Limpia el formulario y resultados */
	public limpiar(): void {
		this.form.reset({
			unidadesRuta: 40,
			vistasMes: 0,
			porcInstaladas: 0,
			diasAscensosSemana: 6,
			ascensosPersonaDia: 2,
			unidadesInstaladas: 8,
			ascensosSemanales: { value: 0, disabled: true },
      diaInicio: 1,
      diaFinal: 30,
		});

		this.ascensosMes = 0;
	}

	/* Calcula unidadesInstaladas desde porcInstaladas */
	private syncFromPercent(): void {
		if (this.syncing) return;

		const uRutaCtrl = this.form.get("unidadesRuta");
		const porcCtrl = this.form.get("porcInstaladas");
		const uInstCtrl = this.form.get("unidadesInstaladas");

		if (!uRutaCtrl || !porcCtrl || !uInstCtrl) return;
		if (!uRutaCtrl.valid || !porcCtrl.valid) {
			this.recalcularAscensos();
			return;
		}

		const unidadesRuta = this.toPositiveInt(uRutaCtrl.value);
		const porc = this.toPositiveInt(porcCtrl.value);

		const unidadesCalculadas = Math.round(unidadesRuta * (porc / 100));

		this.syncing = true;
		uInstCtrl.setValue(unidadesCalculadas, { emitEvent: false });
		this.syncing = false;

		this.recalcularAscensos();
	}

	/* Calcula porcInstaladas desde unidadesInstaladas */
	private syncFromUnitsInstalled(): void {
		if (this.syncing) return;

		const uRutaCtrl = this.form.get("unidadesRuta");
		const porcCtrl = this.form.get("porcInstaladas");
		const uInstCtrl = this.form.get("unidadesInstaladas");

		if (!uRutaCtrl || !porcCtrl || !uInstCtrl) return;
		if (!uRutaCtrl.valid || !uInstCtrl.valid) {
			this.recalcularAscensos();
			return;
		}

		const unidadesRuta = this.toPositiveInt(uRutaCtrl.value);
		let unidadesInstaladas = this.toPositiveInt(uInstCtrl.value);

		/* Normaliza para que no exceda unidadesRuta */
		if (unidadesRuta === 0) {
			unidadesInstaladas = 0;
		} else {
			unidadesInstaladas = Math.min(unidadesInstaladas, unidadesRuta);
		}

		/* Si se normalizó, se refleja sin disparar eventos */
		this.syncing = true;
		uInstCtrl.setValue(unidadesInstaladas, { emitEvent: false });
		this.syncing = false;

		const porc =
			unidadesRuta > 0
				? Math.round((unidadesInstaladas / unidadesRuta) * 100)
				: 0;

		this.syncing = true;
		porcCtrl.setValue(porc, { emitEvent: false });
		this.syncing = false;

		this.recalcularAscensos();
	}

	/* Recalcula ascensos semanales y mensuales */
	private recalcularAscensos(): void {
		const diasCtrl = this.form.get("diasAscensosSemana");
		const perCtrl = this.form.get("ascensosPersonaDia");

		if (!diasCtrl || !perCtrl || !diasCtrl.valid || !perCtrl.valid) {
			this.form
				.get("ascensosSemanales")
				?.setValue(0, { emitEvent: false });
			this.ascensosMes = 0;
			return;
		}

		const diasAscensosSemana = this.toPositiveInt(diasCtrl.value);
		const ascensosPersonaDia = this.toPositiveInt(perCtrl.value);

		const ascensosSemanales = diasAscensosSemana * ascensosPersonaDia;
		this.form
			.get("ascensosSemanales")
			?.setValue(ascensosSemanales, { emitEvent: false });

		this.ascensosMes = this.calcularPorMes(ascensosSemanales);
	}

	private recalcularDiasAbiertos(): void {
		const vistasMesCtrl = this.form.get("vistasMes");
		const DiaFinalCtrl = this.form.get("diaFinal");
		const DiaInicialCtrl = this.form.get("diaInicio");
    const Diferenciadias = DiaFinalCtrl.value - DiaInicialCtrl.value

		const diasAbiertos = Math.round(Diferenciadias / vistasMesCtrl.value) | 0;

		this.form
			.get("diasAbiertos")
			?.setValue(diasAbiertos, { emitEvent: false });
	}
	private recalcularVistasSemana(): void {
		const vistasMesCtrl = this.form.get("vistasMes");

		const diasAbiertos = vistasMesCtrl.value / 4.32;

		this.form
			.get("vistasSemana")
			?.setValue(diasAbiertos, { emitEvent: false });
	}

	/* Normaliza a entero positivo (>= 0) */
	private toPositiveInt(value: any): number {
		const n = Number(value);
		if (!Number.isFinite(n)) return 0;
		const i = Math.floor(n);
		return i >= 0 ? i : 0;
	}

	/* Cálculo mensual con promedio de semanas por mes */
	private calcularPorMes(ascensosSemanales: number): number {
		const semanasPorMes = 4.32;
		return ascensosSemanales * semanasPorMes;
	}

	// Calculo de eventos
	  /* =========================
     NUEVO MODELO (adaptado)
     ========================= */

  /* Construye la distribución del mes (30 días) con ciclo maxUnits */
  private construirDistribucionMes(maxUnits: number, startUnit: number = 1): EventoMes[] {
    if (!Number.isInteger(maxUnits) || maxUnits <= 0) return [];

    const eventos: EventoMes[] = [];
    let eventIndex = 0;

    const u0 = this.normalizarUnidad(startUnit, maxUnits);

    for (let day = 1; day <= 30; day++) {
      if (this.getWeekday(day) === "DOM") continue;

      eventos.push({
        day,
        turno: "a",
        unit: this.unitAtIndex(eventIndex, maxUnits, u0),
      });
      eventIndex++;

      eventos.push({
        day,
        turno: "d",
        unit: this.unitAtIndex(eventIndex, maxUnits, u0),
      });
      eventIndex++;
    }

    return eventos;
  }

  /* Cuenta eventos del rango de días que caen en unidades instaladas (1..unidadesInstaladas) */
  private contarEventosInstaladosEnRangoDias(args: {
    diaInicio: number;
    diaFin: number;
    maxUnits: number;
    unidadesInstaladas: number;
    startUnit?: number;
  }): {
    totalEventosEnDias: number;
    totalEventosEnDiasYUnidades: number;
  } {
    const startUnit = args.startUnit ?? 1;

    const diaInicio = this.clampInt(args.diaInicio, 1, 30);
    const diaFin = this.clampInt(args.diaFin, 1, 30);
    const d1 = Math.min(diaInicio, diaFin);
    const d2 = Math.max(diaInicio, diaFin);

    const maxUnits = this.toPositiveInt(args.maxUnits);
    if (maxUnits <= 0) return { totalEventosEnDias: 0, totalEventosEnDiasYUnidades: 0 };

    const unidadesInstaladas = Math.min(this.toPositiveInt(args.unidadesInstaladas), maxUnits);
    if (unidadesInstaladas <= 0) return { totalEventosEnDias: 0, totalEventosEnDiasYUnidades: 0 };

    const distribucion = this.construirDistribucionMes(maxUnits, startUnit);

    const enDias = distribucion.filter(e => e.day >= d1 && e.day <= d2);

    /* Rango fijo solicitado: 1..unidadesInstaladas */
    const enRangoUnidades = enDias.filter(e => e.unit >= 1 && e.unit <= unidadesInstaladas);

    return {
      totalEventosEnDias: enDias.length,
      totalEventosEnDiasYUnidades: enRangoUnidades.length,
    };
  }

  /* Unidad por índice de evento (0-based) */
  private unitAtIndex(eventIndex: number, maxUnits: number, u0: number): number {
    return 1 + ((u0 - 1 + eventIndex) % maxUnits);
  }

  /* Normaliza unidad a 1..maxUnits */
  private normalizarUnidad(unidad: number, maxUnits: number): number {
    const n = Number(unidad);
    if (!Number.isFinite(n)) return 1;

    const i = Math.floor(n);
    const mod = ((i - 1) % maxUnits + maxUnits) % maxUnits;
    return mod + 1;
  }

  /* Limita entero a [min..max] */
  private clampInt(value: number, min: number, max: number): number {
    const n = Number(value);
    if (!Number.isFinite(n)) return min;

    const i = Math.floor(n);
    if (i < min) return min;
    if (i > max) return max;
    return i;
  }

  private getWeekday(dayOfMonth: number): Weekday {
    const days = ["LUN", "MAR", "MIE", "JUE", "VIE", "SAB", "DOM"] as const;
    const day1: (typeof days)[number] = "LUN";
    const index = (days.indexOf(day1) + (dayOfMonth - 1)) % 7;
    return days[index];
  }
}
